/**
 * The brand layer (docs/NAMING.md §4). Every customer-facing name, domain and
 * address comes from here, and nothing outside this package may hard-code
 * them. The product is Recirculist (recirculist.com), operated by Praxis
 * Bridge LLC. assets/logo.png is the source logo; mark.svg and favicon.svg
 * redraw its tag-and-"e" at icon sizes.
 */

export interface Brand {
  /** Shown as the product name in UI, emails and store listings. */
  displayName: string;
  /** Lowercase wordmark text; `wordmarkAccent` is the part set in the accent colour. */
  wordmark: string;
  wordmarkAccent: string;
  /** The first occurrence of this letter in `wordmark` is drawn as the tag mark, as in the logo. */
  wordmarkTagLetter: string;
  /** One-line description for page titles and meta tags. */
  tagline: string;
  /** The company that operates the service: the data controller and contracting party. */
  legalName: string;
  /** Public marketing site. */
  siteDomain: string;
  /** Other hosts the site answers on, each redirecting to `siteDomain`. */
  siteAliases: string[];
  /** The web app, on a subdomain of `siteDomain` delegated to the edge's account. */
  appDomain: string;
  /** Where people reach a human, including data-protection requests. */
  contactEmail: string;
  /** Sender of transactional email; must be on an SES-verified domain. */
  senderEmail: string;
  /** Country the operating entity is registered in, for legal pages. */
  legalCountry: string;
  colors: {
    ink: string;
    inkSoft: string;
    paper: string;
    card: string;
    rule: string;
    muted: string;
    accent: string;
    tagStock: string;
    tagListed: string;
    tagSold: string;
    tagShipped: string;
    tagAlert: string;
  };
}

export const brand: Brand = {
  displayName: "Recirculist",
  wordmark: "recirculist",
  wordmarkAccent: "list",
  wordmarkTagLetter: "e",
  tagline: "Snap it, list it, sell it. Resale inventory, pricing and profit in one app.",
  legalName: "Praxis Bridge LLC",
  siteDomain: "recirculist.com",
  // www, and the site's first home before the product had a name: links in waitlist emails already sent point there.
  siteAliases: ["www.recirculist.com", "app.praxisbridge.com"],
  appDomain: "app.recirculist.com",
  // The LLC's mailbox; recirculist.com receives no mail yet.
  contactEmail: "info@praxisbridge.com",
  senderEmail: "no-reply@recirculist.com",
  legalCountry: "United States",
  // The product's thrift-store palette (web/src/styles/app.css).
  colors: {
    ink: "#14212b",
    inkSoft: "#3a4a56",
    paper: "#e7e9e2",
    card: "#fcfcf9",
    rule: "#c3c9bd",
    muted: "#67736b",
    accent: "#5f9e77",
    tagStock: "#e5a712",
    tagListed: "#db4f86",
    tagSold: "#2c9463",
    tagShipped: "#3c6ed6",
    tagAlert: "#c0432f",
  },
};

export const siteUrl = `https://${brand.siteDomain}`;
export const appUrl = `https://${brand.appDomain}`;
